import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Fade
} from '@mui/material';
import { motion } from 'framer-motion';

const OTPInput = ({ onSubmit, onResend, loading, mobile, retries, error }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next input if value is entered
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto submit if all digits are entered
    if (newOtp.every(digit => digit !== '') && index === 5) {
      onSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...pastedData.padEnd(6, '')];
      setOtp(newOtp);
      if (newOtp.every(digit => digit !== '')) {
        onSubmit(newOtp.join(''));
      }
    }
  };

  const handleSubmit = () => {
    const otpValue = otp.join('');
    if (otpValue.length === 6) {
      onSubmit(otpValue);
    }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        width: '100%'
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
        Enter OTP
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
        We've sent a 6-digit code to {mobile}
      </Typography>

      <Box
        component={motion.div}
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          width: '100%',
          my: 2
        }}
      >
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={el => inputRefs.current[index] = el}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              style: { textAlign: 'center', fontSize: '1.5rem' },
              type: 'tel',
              pattern: '[0-9]*'
            }}
            sx={{
              width: '3.5rem',
              '& .MuiOutlinedInput-root': {
                height: '3.5rem',
                '& fieldset': {
                  borderWidth: 2,
                  borderColor: error ? 'error.main' : 'primary.main',
                },
                '&:hover fieldset': {
                  borderColor: error ? 'error.main' : 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: error ? 'error.main' : 'primary.main',
                  borderWidth: 2,
                }
              }
            }}
          />
        ))}
      </Box>

      {error && (
        <Fade in={!!error}>
          <Typography color="error" variant="body2" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        </Fade>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%'
        }}
      >
        <Button
          component={motion.button}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={loading || otp.some(digit => digit === '')}
          sx={{
            height: '3rem',
            borderRadius: '1rem',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Verify OTP'
          )}
        </Button>

        <Button
          component={motion.button}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          variant="text"
          color="primary"
          onClick={onResend}
          disabled={loading || retries >= 3}
          sx={{
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Didn't receive the code? Resend
        </Button>

        {retries > 0 && (
          <Typography variant="caption" color="text.secondary">
            {3 - retries} attempts remaining
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default OTPInput; 